/** @jsxImportSource @emotion/react */
import { Modal, Box, Button, CircularProgress, Typography } from '@mui/material'
import _toLower from 'lodash/toLower'
import _get from 'lodash/get'
import { useEffect, useState } from 'react'
import { unwrapResult } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import Images from 'images'
import WrapperBgImg from 'components/wrap-background-img/WrapperBgImg'
import { baseColors, commonClass } from 'theme'
import {
  capitalizeFirstLetter,
  getManaImage,
  getStoneImage,
  getStoneTypeColor,
  toFixedNumber,
} from 'helpers/utils'
import ShoeInfo from 'components/shoe-info/ShoeInfo'
import { useAppDispatch } from 'store'
import { showToast } from 'store/reducers/common'
import { revokeMarketItem } from 'store/reducers/marketList'
import { NFT_TYPE } from 'types/common'
import { IMana, IShoe, IStone } from 'types/nft'
import styles from './RevokeModal.styles'
interface IProps {
  isOpen: boolean
  handleClose: () => void
  itemDetail: IShoe | IMana | IStone | null
  onRevokeSuccess: () => void
  type: string
}

const RevokeModal = ({
  isOpen,
  handleClose,
  itemDetail,
  onRevokeSuccess,
  type,
}: IProps) => {
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setLoading(false)
    }
  }, [isOpen])

  const handleRevoke = () => {
    if (type) {
      const saveObj = {
        itemId: 0,
        type,
      }

      if (type === NFT_TYPE.SHOE) {
        saveObj.itemId = (itemDetail as IShoe).shoeId
      }

      if (type === NFT_TYPE.STONE) {
        saveObj.itemId = (itemDetail as IStone).stoneId
      }

      if (type === NFT_TYPE.MANA) {
        saveObj.itemId = (itemDetail as IMana).manaId
      }
      setLoading(true)
      if (saveObj?.itemId) {
        dispatch(revokeMarketItem(saveObj))
          .then(unwrapResult)
          .then(({ response }) => {
            if (response.apiStatus) {
              onRevokeSuccess()
            } else {
              dispatch(
                showToast({
                  title: 'Error',
                  message: response.message,
                }),
              )
            }
          })
          .finally(() => {
            setLoading(false)
          })
      }
    }
  }

  const renderDetailItem = () => {
    if (type === NFT_TYPE.SHOE) {
      return renderShoeDetail(itemDetail as IShoe)
    }

    if (type === NFT_TYPE.STONE) {
      return renderStoneDetail(itemDetail as IStone)
    }

    if (type === NFT_TYPE.MANA) {
      return renderManaDetail(itemDetail as IMana)
    }

    return null
  }

  const renderStoneDetail = (itemDetail: IStone) => {
    const stoneImg = getStoneImage(itemDetail.type, itemDetail.level)
    const typeText = capitalizeFirstLetter(itemDetail.type)
    const colorType = getStoneTypeColor(typeText)

    return (
      <>
        <div css={styles.bgWrapper}>
          <div css={styles.bgWrapperContent}>
            <img css={styles.shoeImg} src={stoneImg} alt='' className='m-0' />

            <div
              className='mb-2'
              css={styles.shoeId}
              style={{ color: colorType }}
            >
              #{itemDetail?.stoneId}
            </div>

            <Typography css={styles.shoeId} color={baseColors.neutralColor1}>
              +{itemDetail.attribute} | +{itemDetail.awakeningEffect}% | Level{' '}
              {itemDetail.level}
            </Typography>
          </div>
        </div>
      </>
    )
  }

  const renderManaDetail = (itemDetail: IMana) => {
    const qualityText = capitalizeFirstLetter(itemDetail?.quality)
    const qualityColor = _get(baseColors, qualityText + 'Color')

    const manaImg = getManaImage(itemDetail.quality, itemDetail.size)

    return (
      <>
        <div css={styles.bgWrapper}>
          <div css={styles.bgWrapperContent}>
            <img css={styles.shoeImg} src={manaImg} alt='' className='m-0' />

            <div
              className='mb-2'
              css={styles.shoeId}
              style={{ color: qualityColor }}
            >
              #{itemDetail?.manaId}
            </div>
            <Typography css={styles.shoeId} color={baseColors.neutralColor1}>
              {capitalizeFirstLetter(itemDetail.size)} | +{itemDetail.point}{' '}
              point
            </Typography>
          </div>
        </div>
      </>
    )
  }

  const renderSellInfo = (itemDetail: IShoe) => {
    return (
      <div css={styles.confirmInfo}>
        <div css={styles.confirmInfoItem}>
          <span>Date</span>
          <span>
            {itemDetail?.marketSellTime
              ? dayjs(itemDetail.marketSellTime).format('DD-MM-YYYY')
              : ''}
          </span>
        </div>

        <div css={styles.confirmInfoItem}>
          <span>Price</span>
          <span>{toFixedNumber(itemDetail?.marketSellingPrice)} FIT</span>
        </div>
      </div>
    )
  }

  const renderShoeDetail = (shoeDetail: IShoe) => {
    const qualityText = capitalizeFirstLetter(shoeDetail?.quality)
    const qualityColor = _get(baseColors, qualityText + 'Color')
    const qualityBg = (Images.qualityBg as any)[
      _toLower(shoeDetail?.quality) || 'common'
    ] as string

    return (
      <>
        <div css={styles.bgWrapper}>
          <WrapperBgImg src={qualityBg}>
            <div css={styles.bgWrapperContent}>
              <img css={styles.shoeImg} src={shoeDetail?.image} alt='' />

              <div
                className='mb-2'
                css={styles.shoeId}
                style={{ color: qualityColor }}
              >
                #{shoeDetail?.shoeId}
              </div>

              <div className='mb-3 mx-auto' css={styles.shoeInfoWrapper}>
                <ShoeInfo
                  className='mx-auto'
                  level={shoeDetail?.level}
                  shoeClass={shoeDetail?.class}
                  durable={shoeDetail?.durable}
                  color={qualityColor}
                />
              </div>
            </div>
          </WrapperBgImg>
        </div>
      </>
    )
  }

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box css={styles.wrapper}>
        <>
          <div className='df jcb'>
            <div css={styles.modalTitle}>Selling</div>
            <div className='pointer' onClick={handleClose}>
              <Images.CloseIcon />
            </div>
          </div>
          {renderDetailItem()}

          {type === NFT_TYPE.SHOE && renderSellInfo(itemDetail as IShoe)}

          <div css={styles.actions} className='mt-5 mx-auto'>
            <Button
              css={[commonClass.outlineButton, styles.cancelBtn]}
              onClick={handleClose}
            >
              CANCEL
            </Button>
            <Button
              onClick={handleRevoke}
              css={[commonClass.appButton, styles.confirmBtn]}
              disabled={loading}
            >
              {!!loading && <CircularProgress size={18} />}
              REVOKE
            </Button>
          </div>
        </>
      </Box>
    </Modal>
  )
}

export default RevokeModal
